/// <reference types="@types/googlemaps" />

import { Injectable, EventEmitter } from '@angular/core';

import { Location } from '@angular-material-extensions/google-maps-autocomplete';

import { IRoute } from '../interfaces/route';
import { IMap } from '../interfaces/map';
import { ITravelMode } from '../interfaces/travel-mode';

import { Observable, forkJoin, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { IFlight } from '../interfaces/flight';

import codes from '../trips/continent.json';
import { UtilsService } from './utils.service';

@Injectable({
  providedIn: 'root'
})
export class JourneyService {
  public detectChanges: Function;
  public routes: { [route: string]: IRoute } = {};
  public map: IMap;
  public origin: Location;
  public sliderChange = new EventEmitter<number>();
  private flightsSource = new BehaviorSubject<IFlight[]>([]);
  public flights = this.flightsSource.asObservable();
  private flightsUpdated = false;
  private codes = codes;
  private destination: Location;
  private icons = {
    DRIVING: 'car',
    BICYCLING: ['fal', 'bicycle'],
    TRANSIT: 'bus',
    WALKING: ['far', 'walking'],
  };
  private routePopover = {
    DRIVING: null,
    BICYCLING: 'Bike routes may include ferries.',
    TRANSIT: 'Public transport may include Subway, Train, Boats or Trams.',
    WALKING: null,
  };
  private multipliers = {
    co2Kg: {
      DRIVING: 0.146,
      BICYCLING: 0,
      TRANSIT: 0.059,
      WALKING: 0,
    },
    noxG: {
      DRIVING: 0.13,
      BICYCLING: 0,
      TRANSIT: 0.10,
      WALKING: 0,
    },
    cals: {
      DRIVING: 0,
      BICYCLING: 7.1,
      TRANSIT: 0,
      WALKING: 3.2,
    },
    breaths: {
      DRIVING: 12,
      BICYCLING: 20,
      TRANSIT: 12,
      WALKING: 20,
    }
  };
  private _timesPerWeek = 1;

  constructor(
    private utils: UtilsService,
  ) {}

  private setJourneyData(route: IRoute) {
    this.routes[route.mode] = route;
  }

  get timesPerWeek() {
    return this._timesPerWeek;
  }

  set timesPerWeek(timesPerWeek: number) {
    this._timesPerWeek = timesPerWeek;
  }

  updateFlights() {
    if (!this.flightsUpdated) {
      this.flightsUpdated = true;
      const geocoder = new google.maps.Geocoder();

      navigator.geolocation.getCurrentPosition((position) => {
        geocoder.geocode({
          location: new google.maps.LatLng(
            position.coords.latitude,
            position.coords.longitude)
        }, async (result, status) => {

          const countryCode = result[0].address_components.filter(c =>
            c.types.filter(t => t === 'country')[0])[0];

          const flights = await import(`../trips/${countryCode ? this.codes[countryCode.short_name] : 'EU'}.json`);
          this.flightsSource.next(flights.default);
        });
      }, async () => {
        const flights = await import(`../trips/EU.json`);
        this.flightsSource.next(flights.default);
      });
    }
  }

  getCurrentAddress(): Observable<{address: string, latitude: number, longitude: number}> {
    return new Observable(observer => {
      const geocoder = new google.maps.Geocoder();

      navigator.geolocation.getCurrentPosition((position) => {
        geocoder.geocode({
          location: new google.maps.LatLng(
            position.coords.latitude,
            position.coords.longitude)
        }, (result, status) => {
          this.getJourneys();

          observer.next({
            address: result[0].formatted_address,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
          observer.complete();
        });
      });
    });
  }

  getCurrentOrigin(): Observable<{address: string, latitude: number, longitude: number}> {
    return this.getCurrentAddress().pipe(
      map(res =>  {
        this.origin = res;
        this.getJourneys();
        return res;
      })
    );
  }

  getCurrentDestination(): Observable<{address: string, latitude: number, longitude: number}> {
    return this.getCurrentAddress().pipe(
      map(res =>  {
        this.destination = res;
        this.getJourneys();
        return res;
      })
    );
  }

  getDirections(travelMode, customOrigin?: string, customDestination?: string): Observable<google.maps.DirectionsResult> {
    return new Observable(observer => {

      const directionsService = new google.maps.DirectionsService();

      const request = {
        origin: {},
        destination: {},
        travelMode
      };

      request.origin = customOrigin ? customOrigin : {
        lat: this.origin.latitude,
        lng: this.origin.longitude
      };

      request.destination = customDestination ? customDestination : {
        lat: this.destination.latitude,
        lng: this.destination.longitude,
      },

      directionsService.route(request, (response => {
        observer.next(response);
        observer.complete();
      }));
    });
  }

  getJourneys() {
    const loading  = Object.keys(this.routes).filter(r => this.routes[r].loading);
    if (this.map && loading.length || !(this.origin && this.destination)) {
      return new Observable(obs => {
        this.map = null;
        obs.next();
        obs.complete();
      });
    }

    this.onLoadingData();

    return forkJoin([
      this.setJourney(google.maps.TravelMode.DRIVING),
      this.setJourney(google.maps.TravelMode.BICYCLING),
      this.setJourney(google.maps.TravelMode.TRANSIT),
      this.setJourney(google.maps.TravelMode.WALKING)
    ]).pipe(map(res => {
      this.utils.setPopovers();
      return res;
    }));
  }

  selectOrigin(location: Location) {
    this.origin = location;
    return this.getJourneys();
  }

  hasRoute() {
    return this.origin && this.destination;
  }

  selectDestination(location: Location) {
    this.destination = location;
    return this.getJourneys();
  }

  setJourney(travelMode) {
    return new Observable(observer => {
      this.getDirections(travelMode).subscribe((res: any) => {
        let journey: IRoute = {
          available: false,
          icon: this.icons[travelMode],
          co2Kg: null,
          noxG: null,
          cals: null,
          mode: travelMode,
          loading: false,
          duration: null,
          durationSecs: null,
          distance: null,
        };
        if (res && res.routes && res.routes[0] && res.routes[0].legs && res.routes[0].legs[0]) {
          const distance = res.routes[0].legs[0].distance.value / 1000;
          const duration = this.formatDuration(res.routes[0].legs[0].duration.text);
          const durationMinutes = res.routes[0].legs[0].duration.value / 60;

          journey = {
            available: true,
            icon: this.icons[travelMode],
            co2Kg: Number((distance * this.multipliers.co2Kg[travelMode]).toFixed(1)),
            noxG: Number((distance * this.multipliers.noxG[travelMode]).toFixed(1)),
            cals: Math.round(durationMinutes * this.multipliers.cals[travelMode] * (3.5 * 76 / 200)),
            mode: travelMode,
            loading: false,
            duration,
            durationSecs: res.routes[0].legs[0].duration.value,
            distance: Number(distance.toFixed(1)),
            breaths: Number(durationMinutes * this.multipliers.breaths[travelMode]),
          };

          if (travelMode === 'TRANSIT') {
            const transitEmissions = {co2Kg: 0, noxG: 0, cals: 0, breaths: 0};
            res.routes[0].legs[0].steps.map(step => {
              transitEmissions.co2Kg += (step.distance.value / 1000 * this.multipliers.co2Kg[step.travel_mode]);
              transitEmissions.noxG += (step.distance.value / 1000 * this.multipliers.noxG[step.travel_mode]);
              transitEmissions.cals += (step.duration.value / 60 * this.multipliers.cals[step.travel_mode] * (3.5 * 76 / 200));
              transitEmissions.breaths += (step.duration.value / 60 * this.multipliers.breaths[step.travel_mode]);
            });

            journey.co2Kg = Number(transitEmissions.co2Kg.toFixed(1));
            journey.noxG = Number(transitEmissions.noxG.toFixed(1));
            journey.cals = Math.round(transitEmissions.cals);
            journey.breaths = transitEmissions.breaths;
          }
        }
        journey.popover = this.routePopover[travelMode];
        this.setJourneyData(journey);
        this.detectChanges();
        observer.next();
        observer.complete();
      });
    });
  }

  formatDuration(duration) {
    duration = duration.replace(' hours', 'h');
    duration = duration.replace(' hour', 'h');
    duration = duration.replace(' mins', 'm');
    duration = duration.replace(' min', 'm');
    return duration;
  }

  private onLoadingData() {
    this.map = null;

    this.setJourneyData({
      icon: this.icons.DRIVING,
      loading: true,
      mode: 'DRIVING',
    });

    this.setJourneyData({
      icon: this.icons.BICYCLING,
      loading: true,
      mode: 'BICYCLING',
    });

    this.setJourneyData({
      icon: this.icons.TRANSIT,
      loading: true,
      mode: 'TRANSIT',
    });

    this.setJourneyData({
      icon: this.icons.WALKING,
      loading: true,
      mode: 'WALKING',
    });
  }

  setMap(mode: ITravelMode) {
    this.map = {
      origin: this.origin ? `${this.origin.latitude},${this.origin.longitude}` : null,
      destination: this.destination ?  `${this.destination.latitude},${this.destination.longitude}` : null,
      mode
    };
  }
}
